import { useFormik } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { IoClose } from 'react-icons/io5';
import { ActionIcon, Button, Modal, Select, Text } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import RequiredInput from '../../../components/forms/InputHOC';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { DocumentTypeService } from '../../../services/document-type/documentTypeService';
import { DocumentType, DocumentTypeFieldsModel } from '../../../type/entities/document-type';

interface DocumentTypeModalFormProps {
    data?: DocumentType | null;
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}

interface DocumentTypeFormData {
    name: string;
    entity: string;
}

export const documentTypeEntityOptions = [
    { label: 'Contrato', value: 'contract' },
    { label: 'Usuario', value: 'user' },
    { label: 'Tutor', value: 'tutor' },
    { label: 'Compañía', value: 'company' },
    { label: 'Incidencia', value: 'incidence' },
    { label: 'Factura', value: 'invoice' },
    { label: 'Producto', value: 'product'}
];

const schema = yup.object({
    name: yup.string().min(1, 'Demasiado corto').max(255, 'Demasiado largo').required('Campo obligatorio'),
    entity: yup.string().required('Campo obligatorio'),
});

const DocumentTypeModalForm: React.FC<DocumentTypeModalFormProps> = ({ data, isOpen, onClose, refetch }) => {
    const { handleErrors } = useHandleErrors();
    const documentTypeService = new DocumentTypeService();
    const isEditMode = Boolean(data?.id);

    const entityOptions = useMemo(() => {
        if (!data?.entity || documentTypeEntityOptions.some((option) => option.value === data.entity)) {
            return documentTypeEntityOptions;
        }

        return [{ label: `${data.entity} (actual)`, value: data.entity }, ...documentTypeEntityOptions];
    }, [data?.entity]);

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    const create = async (values: DocumentTypeFormData) => {
        const payload: DocumentTypeFieldsModel = {
            name: values.name.trim(),
            entity: values.entity,
            immutable: false,
        };

        try {
            const response = await (await documentTypeService.createDocumentType(payload)).getResponseData();
            if (response.success) {
                toast.success('Tipo de documento creado correctamente');
                refetch();
                handleClose();
                return;
            }

            handleErrors(response);
        } catch (error: any) {
            toast.error(error?.message ?? 'Error al crear el tipo de documento');
        }
    };

    const edit = async (values: DocumentTypeFormData) => {
        if (!data?.id) {
            return;
        }

        const payload: DocumentTypeFieldsModel = {
            documentTypeId: data.id,
            name: values.name.trim(),
            entity: values.entity,
            immutable: Boolean(data.immutable),
        };

        try {
            const response = await (await documentTypeService.editDocumentType(payload)).getResponseData();
            if (response.success) {
                toast.success('Tipo de documento actualizado correctamente');
                refetch();
                handleClose();
                return;
            }

            handleErrors(response);
        } catch (error: any) {
            toast.error(error?.message ?? 'Error al editar el tipo de documento');
        }
    };

    const formik = useFormik<DocumentTypeFormData>({
        initialValues: {
            name: '',
            entity: '',
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: async (values) => {
            if (isEditMode) {
                await edit(values);
                return;
            }

            await create(values);
        },
    });

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        formik.setValues({
            name: data?.name || '',
            entity: data?.entity || '',
        });
    }, [data, isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size='md'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-2xl !shadow-xl'
            className='z-[999] [&_.pointer-events-none]:overflow-visible'
        >
            <form onSubmit={formik.handleSubmit}>
                <div className='m-auto px-7 pt-6 pb-4'>
                    <div className='mb-7 flex items-center justify-between flex-shrink-0'>
                        <Text className="text-xl">{isEditMode ? 'Editar' : 'Crear'} tipo de documento</Text>
                        <ActionIcon size='sm' variant='text' onClick={handleClose}>
                            <IoClose className='h-auto w-6' strokeWidth={1.8} />
                        </ActionIcon>
                    </div>

                    <div className='grid grid-cols-1 gap-4'>
                        <RequiredInput
                            id='name'
                            name='name'
                            label='Nombre'
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            error={formik.errors.name}
                        />

                        <Select
                            label='Entidad'
                            placeholder='Selecciona la entidad'
                            options={entityOptions}
                            value={entityOptions.find((option) => option.value === formik.values.entity) || null}
                            onChange={(selectedOption: any) => formik.setFieldValue('entity', selectedOption?.value || '')}
                            error={formik.errors.entity}
                        />
                    </div>

                    <div className='mt-8 flex justify-end gap-3'>
                        <Button
                            type='button'
                            variant='outline'
                            onClick={handleClose}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type='submit'
                            isLoading={formik.isSubmitting}
                            disabled={formik.isSubmitting}
                        >
                            {isEditMode ? 'Actualizar' : 'Crear'}
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    );
};

export default DocumentTypeModalForm;
